<template>
  <article>
    <div v-if="isPreview" class="preview-badge">Preview</div>
    <NuxtLink :to="post._path" class="image-link">
      <img :src="imageConfig.src" :alt="post.title" :class="imageConfig.class">
    </NuxtLink>
    <div class="card-content">
      <h3>
        <NuxtLink :to="post._path">{{ removeQuotes(post.title) }}</NuxtLink>
      </h3>
      <div class="post-meta">
        <span v-if="post.date">
          {{ formatDate(post.date) }}
        </span>
        <span v-if="post.readingTime">
          {{ post.readingTime }}
        </span>
      </div>
      <div v-if="post.category" class="category-badges">
        <template v-if="Array.isArray(post.category)">
          <NuxtLink 
            v-for="cat in post.category" 
            :key="cat"
            :to="`/blog/category/${cat}`"
            class="category-badge"
          >
            {{ cat }}
          </NuxtLink>
        </template>
        <template v-else>
          <NuxtLink :to="`/blog/category/${post.category}`" class="category-badge">
            {{ post.category }}
          </NuxtLink>
        </template>
      </div>
      <p class="description">{{ removeQuotes(post.description) }}</p>
      <NuxtLink :to="post._path" class="btn btn-primary">Read more</NuxtLink>
    </div>
  </article>
</template>

<script setup>
const props = defineProps({
  post: {
    type: Object,
    required: true
  }
});

const isPreview = computed(() => {
  if (process.env.NODE_ENV !== 'development' || !props.post.date) {
    return false;
  }
  const postDate = new Date(props.post.date);
  const now = new Date();
  return postDate > now;
});

// Format date to MM/DD/YYYY
const imageConfig = computed(() => {
  // Use thumbnailImage first, then heroImage, then fallback to image
  const thumbnailImage = props.post.thumbnailImage || props.post.heroImage || props.post.image;
  
  if (thumbnailImage) {
    return { src: thumbnailImage, class: 'card-img-full' };
  }

  const rawCategory = Array.isArray(props.post.category)
    ? props.post.category[0]
    : props.post.category;
  const category = rawCategory?.toLowerCase();
  const defaultImage = { src: 'https://cdn-media.geekcafe.com/assets/img/placeholders/default-placeholder.svg', fullWidth: true };

  const categoryMap = {
    aws: { src: 'https://cdn.worldvectorlogo.com/logos/aws-2.svg', fullWidth: false },
    python: { src: 'https://cdn.worldvectorlogo.com/logos/python-5.svg', fullWidth: false },
    dotnet: { src: 'https://cdn.worldvectorlogo.com/logos/dotnet.svg', fullWidth: false },
    ai: { src: '/img/blog-posts/categories/ai-placeholder.svg', fullWidth: true },
    windsurf: { src: '/img/logos/windsurf-logo.svg', fullWidth: false },
    cursor: { src: '/img/logos/cursor-logo.svg', fullWidth: false },
    vscodium: { src: '/img/logos/vscodium-logo.svg', fullWidth: false },
    devops: { src: '/img/blog-posts/categories/devops.svg', fullWidth: false },
    git: { src: '/img/logos/git-logo.svg', fullWidth: false },
    'developer-tools': { src: '/img/blog-posts/categories/developer-tools.png', fullWidth: false },
    security: { src: '/img/blog-posts/categories/security.png', fullWidth: true },
    trends: { src: '/img/blog-posts/categories/trends2.png', fullWidth: true },
    'best-practices': { src: '/img/blog-posts/categories/best-practices2.png', fullWidth: true },
    'web-development': { src: '/img/blog-posts/categories/web-development.png', fullWidth: true },
    'database': { src: '/img/blog-posts/categories/database.png', fullWidth: true },
    'dynamodb': { src: '/images/categories/dynamodb.svg', fullWidth: true },
    'lambda': { src: '/images/categories/lambda.svg', fullWidth: true },
    'cloudfront': { src: '/images/categories/cloudfront.svg', fullWidth: true },
    'cloud-development-kit': { src: '/images/categories/cloud-development-kit.svg', fullWidth: true },
    'aws-cdk': { src: '/images/categories/aws-cdk.svg', fullWidth: true },
  };

  const config = categoryMap[category] || defaultImage;
  
  return {
    src: config.src,
    class: config.fullWidth ? 'card-img-full' : 'card-img-logo'
  };
});

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC' // Treat the date as UTC to prevent timezone shifts
  })
}

function removeQuotes(text) {
  if (!text) return text
  return text.replace(/^"+|"+$/g, '')
}
</script>

<style scoped>
article {
  position: relative; /* Required for badge positioning */
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  display: flex;
  flex-direction: column;
}

article:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.image-link {
  display: block;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.image-link img {
  width: 100%;
  height: 250px;
  display: block;
  transition: transform 0.3s;
}

.image-link:hover img {
  transform: scale(1.05);
}

.card-img-full {
  object-fit: cover;
  padding: 0;
}

.card-img-logo {
  object-fit: scale-down;
  padding: 2rem;
  background-color: #f8f9fa;
}

.card-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

h3 {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  line-height: 1.3;
}

h3 a {
  color: #333;
  text-decoration: none;
  transition: color 0.2s;
}

h3 a:hover {
  color: #007bff;
}

.post-meta {
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.post-meta span {
  display: inline-flex;
  align-items: center;
}

.post-meta span:not(:last-child)::after {
  content: "•";
  margin-left: 0.5rem;
  color: #ccc;
}

.post-meta a {
  color: #007bff;
  text-decoration: none;
}

.post-meta a:hover {
  text-decoration: underline;
}

.category-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.category-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-decoration: none;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.category-badge:hover {
  background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
  color: white;
  text-decoration: none;
}

.description {
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex: 1;
}

.btn-primary {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  transition: background 0.2s;
  align-self: flex-start;
  margin-top: auto;
  font-weight: 500;
}

.btn-primary:hover {
  background: #0056b3;
  color: white;
  text-decoration: none;
}
.preview-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background-color: #ffc107; /* A nice warning yellow */
  color: #212529; /* Dark text for contrast */
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}
</style>
